import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, Drawer, Stack, Select, MenuItem,
  TextField, Button, CircularProgress, Chip, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, Tooltip,
  FormControl, InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../api';

// ─── Provider / model catalogue ──────────────────────────────────────────────
const PROVIDERS = {
  openai: {
    label: 'OpenAI',
    color: '#10a37f',
    models: [
      { id: 'gpt-4o',       label: 'GPT-4o' },
      { id: 'gpt-4o-mini',  label: 'GPT-4o Mini' },
      { id: 'gpt-4-turbo',  label: 'GPT-4 Turbo' },
    ],
  },
  anthropic: {
    label: 'Anthropic',
    color: '#d97706',
    models: [
      { id: 'claude-opus-4-6',          label: 'Claude Opus 4.6' },
      { id: 'claude-sonnet-4-6',         label: 'Claude Sonnet 4.6' },
      { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    ],
  },
  gemini: {
    label: 'Google Gemini',
    color: '#4285f4',
    models: [
      { id: 'gemini-2.0-flash',   label: 'Gemini 2.0 Flash' },
      { id: 'gemini-1.5-pro',     label: 'Gemini 1.5 Pro' },
      { id: 'gemini-1.5-flash',   label: 'Gemini 1.5 Flash' },
    ],
  },
};

// ─── Build profile context for display ───────────────────────────────────────
function buildProfileContext(columns, profileData) {
  return columns.map(col => {
    const profile = profileData[col.profileId];
    if (!profile) return null;
    const version = profile.versions.find(v => v.version_id === col.versionId);
    if (!version) return null;
    return { person_name: profile.person_name, version };
  }).filter(Boolean);
}

// ─── Simple markdown-ish renderer ────────────────────────────────────────────
function MessageText({ text }) {
  // Bold **text**, headings ## text, bullets - text
  const lines = text.split('\n');
  return (
    <Box>
      {lines.map((line, i) => {
        if (line.startsWith('## ')) {
          return <Typography key={i} sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--tx-hi)', mt: i > 0 ? 1 : 0 }}>{line.slice(3)}</Typography>;
        }
        if (line.startsWith('### ')) {
          return <Typography key={i} sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#cbd5e1', mt: i > 0 ? 0.5 : 0 }}>{line.slice(4)}</Typography>;
        }
        if (line.match(/^[-*] /)) {
          return (
            <Stack key={i} direction="row" gap={0.75} alignItems="flex-start" sx={{ mt: 0.25 }}>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#6366f1', flexShrink: 0, mt: '6px' }} />
              <Typography sx={{ fontSize: '0.78rem', color: 'var(--tx-lo)', lineHeight: 1.55 }}>{renderBold(line.slice(2))}</Typography>
            </Stack>
          );
        }
        if (line === '') return <Box key={i} sx={{ height: 6 }} />;
        return (
          <Typography key={i} sx={{ fontSize: '0.78rem', color: 'var(--tx-lo)', lineHeight: 1.55 }}>
            {renderBold(line)}
          </Typography>
        );
      })}
    </Box>
  );
}

function renderBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} style={{ color: 'var(--tx-md)' }}>{part.slice(2, -2)}</strong>
      : part
  );
}

// ─── Key Settings Dialog ──────────────────────────────────────────────────────
function KeySettingsDialog({ open, onClose, keyStatus, onKeySaved, onKeyDeleted }) {
  const [editing, setEditing] = useState(null); // provider id being edited
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleSave(provider) {
    if (!inputVal.trim()) return;
    setSaving(true);
    setErr('');
    try {
      await api.put(`/api/llm-keys/${provider}`, { api_key: inputVal.trim() });
      onKeySaved(provider);
      setEditing(null);
      setInputVal('');
    } catch {
      setErr('Failed to save key');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(provider) {
    try {
      await api.delete(`/api/llm-keys/${provider}`);
      onKeyDeleted(provider);
    } catch {
      setErr('Failed to remove key');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#0d1526', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 2 } }}>
      <DialogTitle sx={{ color: 'var(--tx-hi)', fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <SettingsIcon sx={{ color: '#6366f1', fontSize: 18 }} />
          LLM API Keys
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', mb: 2 }}>
          Keys are stored securely in Cloudflare KV and never exposed to the browser.
        </Typography>
        <Stack gap={2}>
          {Object.entries(PROVIDERS).map(([pid, prov]) => {
            const hasKey = keyStatus[pid];
            const isEditing = editing === pid;
            return (
              <Box key={pid} sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1.5, p: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={isEditing ? 1.5 : 0}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: prov.color }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--tx-md)' }}>{prov.label}</Typography>
                    {hasKey
                      ? <CheckCircleIcon sx={{ fontSize: 14, color: '#34d399' }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.25)' }} />}
                  </Stack>
                  <Stack direction="row" gap={0.5}>
                    {hasKey && !isEditing && (
                      <Tooltip title="Remove key">
                        <IconButton size="small" onClick={() => handleDelete(pid)} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#fb7185' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!isEditing && (
                      <Button size="small" onClick={() => { setEditing(pid); setInputVal(''); }}
                        sx={{ fontSize: '0.72rem', color: '#818cf8', borderColor: 'rgba(99,102,241,0.4)', '&:hover': { borderColor: '#6366f1' } }}
                        variant="outlined">
                        {hasKey ? 'Replace' : 'Add Key'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
                {isEditing && (
                  <Stack direction="row" gap={1} alignItems="center">
                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      type="password"
                      placeholder={`Paste ${prov.label} API key…`}
                      value={inputVal}
                      onChange={e => setInputVal(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSave(pid); if (e.key === 'Escape') setEditing(null); }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', color: 'var(--tx-md)', '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' } } }}
                    />
                    <Button size="small" variant="contained" onClick={() => handleSave(pid)} disabled={saving || !inputVal.trim()}
                      sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, fontSize: '0.72rem', whiteSpace: 'nowrap', minWidth: 60 }}>
                      {saving ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => setEditing(null)} sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                      Cancel
                    </Button>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Chat Panel ──────────────────────────────────────────────────────────
export default function CareerChatPanel({ open, onClose, columns, profileData }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [provider, setProvider] = useState('openai');
  const [model, setModel]       = useState('gpt-4o');
  const [keyStatus, setKeyStatus] = useState({ openai: false, anthropic: false, gemini: false });
  const [keyOpen, setKeyOpen]   = useState(false);
  const [error, setError]       = useState('');
  const bottomRef = useRef(null);

  // Load key status
  useEffect(() => {
    if (!open) return;
    api.get('/api/llm-keys')
      .then(res => setKeyStatus(res.data.providers || {}))
      .catch(() => {});
  }, [open]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // When provider changes, default to first model
  const handleProviderChange = useCallback((pid) => {
    setProvider(pid);
    setModel(PROVIDERS[pid].models[0].id);
  }, []);

  const profiles = buildProfileContext(columns, profileData);

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const res = await api.post('/api/career/chat', {
        messages: newMessages,
        profiles,
        provider,
        model,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      const msg = e.response?.data?.error || 'Request failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const providerInfo = PROVIDERS[provider];
  const activeProfiles = profiles.length;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 480,
            bgcolor: 'var(--bg-surf)',
            borderLeft: '1px solid rgba(99,102,241,0.2)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={1}>
              <SmartToyIcon sx={{ color: '#6366f1', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: 'var(--tx-hi)', fontSize: '1rem' }}>Career AI Chat</Typography>
              {activeProfiles > 0 && (
                <Chip label={`${activeProfiles} profile${activeProfiles !== 1 ? 's' : ''}`} size="small"
                  sx={{ height: 18, fontSize: '0.62rem', bgcolor: 'rgba(99,102,241,0.18)', color: '#a5b4fc' }} />
              )}
            </Stack>
            <Stack direction="row" gap={0.5}>
              <Tooltip title="Manage API keys">
                <IconButton size="small" onClick={() => setKeyOpen(true)} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#a5b4fc' } }}>
                  <SettingsIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' } }}>
                <CloseIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Stack>
          </Stack>

          {/* Provider + Model selector */}
          <Stack direction="row" gap={1} mt={1.5}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={provider}
                onChange={e => handleProviderChange(e.target.value)}
                sx={selectSx}
                renderValue={v => (
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: PROVIDERS[v].color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-md)' }}>{PROVIDERS[v].label}</Typography>
                    {keyStatus[v]
                      ? <CheckCircleIcon sx={{ fontSize: 12, color: '#34d399', ml: 'auto' }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', ml: 'auto' }} />}
                  </Stack>
                )}
              >
                {Object.entries(PROVIDERS).map(([pid, prov]) => (
                  <MenuItem key={pid} value={pid} sx={{ fontSize: '0.78rem' }}>
                    <Stack direction="row" alignItems="center" gap={1} width="100%">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: prov.color }} />
                      {prov.label}
                      {keyStatus[pid]
                        ? <CheckCircleIcon sx={{ fontSize: 13, color: '#34d399', ml: 'auto' }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.25)', ml: 'auto' }} />}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ flex: 1 }}>
              <Select value={model} onChange={e => setModel(e.target.value)} sx={selectSx}>
                {providerInfo.models.map(m => (
                  <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.78rem' }}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* No key warning */}
          {!keyStatus[provider] && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}
              sx={{ bgcolor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 1, px: 1.25, py: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#fbbf24' }}>No API key for {providerInfo.label}</Typography>
              <Button size="small" onClick={() => setKeyOpen(true)}
                sx={{ fontSize: '0.68rem', color: '#fbbf24', p: '2px 8px', minWidth: 0 }}>Add Key</Button>
            </Stack>
          )}

          {/* No profiles warning */}
          {activeProfiles === 0 && (
            <Box sx={{ mt: 1, bgcolor: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 1, px: 1.25, py: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#818cf8' }}>Add profiles to the comparison to chat about them.</Typography>
            </Box>
          )}
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {messages.length === 0 && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <SmartToyIcon sx={{ fontSize: 40, color: 'rgba(99,102,241,0.25)', mb: 1 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem' }}>
                Ask anything about the loaded career profiles.
              </Typography>
              <Stack gap={0.75} mt={2.5} alignItems="flex-start" sx={{ textAlign: 'left' }}>
                {SUGGESTIONS.map((s, i) => (
                  <Box key={i} onClick={() => setInput(s)}
                    sx={{ cursor: 'pointer', bgcolor: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 1.5, px: 1.5, py: 0.75, width: '100%', '&:hover': { bgcolor: 'rgba(99,102,241,0.14)', borderColor: 'rgba(99,102,241,0.4)' } }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#818cf8' }}>{s}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {messages.map((msg, i) => (
            <Box key={i} sx={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: msg.role === 'user' ? '85%' : '100%',
            }}>
              {msg.role === 'user' ? (
                <Box sx={{ bgcolor: 'rgba(99,102,241,0.22)', border: '1px solid rgba(99,102,241,0.35)', borderRadius: '12px 12px 2px 12px', px: 1.75, py: 1 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: 'var(--tx-md)', lineHeight: 1.5 }}>{msg.content}</Typography>
                </Box>
              ) : (
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '2px 12px 12px 12px', px: 1.75, py: 1.25 }}>
                  <Stack direction="row" alignItems="center" gap={0.75} mb={0.75}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: providerInfo.color }} />
                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', fontWeight: 600, letterSpacing: '0.04em' }}>
                      {providerInfo.label} · {model}
                    </Typography>
                  </Stack>
                  <MessageText text={msg.content} />
                </Box>
              )}
            </Box>
          ))}

          {loading && (
            <Box sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '2px 12px 12px 12px', px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={12} sx={{ color: '#6366f1' }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' }}>Thinking…</Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ fontSize: '0.75rem' }} onClose={() => setError('')}>{error}</Alert>
          )}

          <div ref={bottomRef} />
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />

        {/* Input */}
        <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
          <Stack direction="row" gap={1} alignItems="flex-end">
            <TextField
              multiline
              maxRows={4}
              fullWidth
              size="small"
              placeholder="Ask about these careers…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.82rem', color: 'var(--tx-md)', bgcolor: 'rgba(255,255,255,0.04)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
                '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(255,255,255,0.25)', opacity: 1 },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || loading || !keyStatus[provider]}
              sx={{ bgcolor: '#6366f1', color: '#fff', borderRadius: 1.5, width: 38, height: 38, flexShrink: 0, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: 'rgba(99,102,241,0.25)', color: 'rgba(255,255,255,0.3)' } }}
            >
              {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Stack>
          <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.18)', mt: 0.75 }}>
            Enter to send · Shift+Enter for new line
          </Typography>
        </Box>
      </Drawer>

      <KeySettingsDialog
        open={keyOpen}
        onClose={() => setKeyOpen(false)}
        keyStatus={keyStatus}
        onKeySaved={provider => setKeyStatus(prev => ({ ...prev, [provider]: true }))}
        onKeyDeleted={provider => setKeyStatus(prev => ({ ...prev, [provider]: false }))}
      />
    </>
  );
}

const selectSx = {
  color: 'var(--tx-md)',
  fontSize: '0.75rem',
  bgcolor: 'rgba(255,255,255,0.04)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,102,241,0.4)' },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
};

const SUGGESTIONS = [
  'Summarise the career progression for each profile',
  'What functions have these people worked in?',
  'Who has the most leadership experience?',
  'Compare the career trajectories across these profiles',
  'What are the key differences in their backgrounds?',
];
